/**
 * Blockades and war bands in the player's system (M5 spec §6, §8.4, §14.3; seam S-M3-11; plan C15).
 *
 * War bands (a gunship and two interceptors of a warring faction) lurk at the relays of systems shared with the
 * enemy; the core lists them in `LocalManifest.warBands` and traffic materializes them there like any agent. When
 * the local system is **blockaded** (a `blockade` state at one of `activeStages`, charter-conflict.json) and a band
 * there is hostile to the player, M3's interdiction encounter may pull the player out beside it: the encounter port
 * (`traffic/encounterPort.ts`) adds `ratePerMin` to the interdiction rate and its interdictor group is the band's
 * ships. Otherwise nothing changes.
 *
 * The state is fed at message rate (manifests, the 1 Hz system view); the port asks per encounter roll.
 */
import { z } from 'zod'
import conflictJson from '../data/charter-conflict.json'
import { isHostile, PLAYER_TEAM_ID } from '../combat/hostility'
import { systemId as systemIdAt } from '../galaxy/placement'
import type { SpawnShipSpec } from '../reach/runtime/types'
import type { FactionId, LocalManifest, SystemEconomyView } from '../strategic/types'
import type { ContactStore } from '../traffic/contacts'

const blockadeConfigSchema = z
  .object({
    ratePerMin: z.number().nonnegative(),
    activeStages: z.array(z.enum(['pending', 'active', 'recovering'])).min(1),
  })
  .strict()
export type BlockadeConfig = z.infer<typeof blockadeConfigSchema>

export const BLOCKADE_CONFIG: BlockadeConfig = blockadeConfigSchema.parse(conflictJson.blockade)

export interface BlockadeState {
  /** The local system (the last manifest's), or null outside the Reach's economy. */
  systemId: string | null
  /** True while the local system's blockade state is at an active stage. */
  blockaded: boolean
  /** The manifest's war band agent ids. */
  readonly bandIds: string[]
  /** The local agent store the bands fly in (the conflict runtime sets it), or null. */
  store: ContactStore | null
}

export function createBlockadeState(): BlockadeState {
  return { systemId: null, blockaded: false, bandIds: [], store: null }
}

/** The live blockade picture (the conflict runtime feeds it; the encounter port reads it). */
export const liveBlockade: BlockadeState = createBlockadeState()

/** A new system: its war bands; the blockade flag waits for the system view. */
export function applyBlockadeManifest(b: BlockadeState, m: Pick<LocalManifest, 'systemId' | 'warBands'>): void {
  if (b.systemId !== m.systemId) b.blockaded = false
  b.systemId = m.systemId
  b.bandIds.length = 0
  for (const id of m.warBands) b.bandIds.push(id)
}

/** The system view's states: blockaded while a `blockade` state is at an active stage. */
export function applyBlockadeView(b: BlockadeState, v: Pick<SystemEconomyView, 'systemId' | 'claim'>, cfg: BlockadeConfig = BLOCKADE_CONFIG): void {
  if (v.systemId !== b.systemId) return
  b.blockaded = v.claim.states.some((s) => s.kind === 'blockade' && cfg.activeStages.includes(s.stage))
}

export function clearBlockade(b: BlockadeState): void {
  b.systemId = null
  b.blockaded = false
  b.bandIds.length = 0
}

/** The band's ships in space now (slots of the store), hostile to the player, as spawn specs; empty when none. */
export function hostileBand(b: BlockadeState, store: ContactStore | null): SpawnShipSpec[] {
  const out: SpawnShipSpec[] = []
  if (store === null) return out
  for (const id of b.bandIds) {
    const slot = store.slotById.get(id)
    if (slot === undefined || !isHostile(store.team[slot], PLAYER_TEAM_ID)) continue
    const def = store.shipDef[slot]
    if (def === '') continue
    const spec = out.find((s) => s.def === def)
    if (spec === undefined) out.push({ def, profile: 'veteran', count: 1 })
    else spec.count++
  }
  return out
}

/** The faction of the band (its first ship still in the store), or null. */
export function bandFaction(b: BlockadeState, store: ContactStore | null): FactionId | null {
  if (store === null) return null
  for (const id of b.bandIds) {
    const slot = store.slotById.get(id)
    if (slot !== undefined) return store.faction[slot]
  }
  return null
}

/** What the encounter port asks of the blockade (S-M3-11). */
export interface BlockadeSource {
  /** Extra interdictions per minute in `systemIndex` (0 unless it is the blockaded local system with a hostile band). */
  extraRatePerMin(systemIndex: number): number
  /** The band as the interdictor group in `systemIndex`, or null (the director's own group then). */
  group(systemIndex: number): readonly SpawnShipSpec[] | null
}

/** A source over a blockade state and its local agent store. */
export function createBlockadeSource(
  b: BlockadeState,
  cfg: BlockadeConfig = BLOCKADE_CONFIG,
  systemIdOf: (index: number) => string = systemIdAt,
): BlockadeSource {
  const active = (systemIndex: number): readonly SpawnShipSpec[] | null => {
    if (!b.blockaded || b.systemId === null || systemIdOf(systemIndex) !== b.systemId) return null
    const band = hostileBand(b, b.store)
    return band.length > 0 ? band : null
  }
  return {
    extraRatePerMin: (systemIndex) => (active(systemIndex) === null ? 0 : cfg.ratePerMin),
    group: (systemIndex) => active(systemIndex),
  }
}

/** The live source the encounter port uses by default. */
export const liveBlockadeSource: BlockadeSource = createBlockadeSource(liveBlockade)
